/**
 * Pure derived-data functions for Unity audio playback.
 * No store imports — independently testable.
 * Follows the same pattern as projectDerived.ts.
 */
import type { TimelineProject, AudioClip } from '@/types/timeline.ts';
import { getClipAbsoluteStart } from '@/store/projectDerived';

export interface SceneBoundary {
  sceneId: number;
  startTime: number;
  endTime: number;
}

export interface AudioClipSnapshot {
  id: number;
  startTime: number;
  duration: number;
  volume: number;
  bufferOffset: number;
  lane: number;
  isMuted: boolean;
  fadeInTime: number;
  fadeOutTime: number;
  sceneId: number;
}

/**
 * Compute time boundaries for every scene in the project.
 * Scene duration = sum of its shots' durations.
 */
export function getSceneBoundaries(project: TimelineProject): SceneBoundary[] {
  const boundaries: SceneBoundary[] = [];
  let offset = 0;
  for (const scene of project.scenes) {
    const duration = scene.shots.reduce((sum, s) => sum + s.duration, 0);
    boundaries.push({ sceneId: scene.id, startTime: offset, endTime: offset + duration });
    offset += duration;
  }
  return boundaries;
}

/**
 * Find which scene a given time falls within.
 * Returns the scene's id, or the last scene if past the end.
 */
export function getSceneIdAtTime(boundaries: SceneBoundary[], time: number): number {
  for (const b of boundaries) {
    if (time >= b.startTime && time < b.endTime) return b.sceneId;
  }
  // Past the end — return last scene
  return boundaries.length > 0 ? boundaries[boundaries.length - 1].sceneId : 0;
}

/**
 * Build the clip metadata snapshot for the playTimeline bridge message.
 * Includes clips whose playback range overlaps the given scene window.
 * Each clip is assigned to the scene where its startTime falls.
 */
export function buildClipSnapshot(
  clips: AudioClip[],
  boundaries: SceneBoundary[],
  windowSceneIds: Set<number>,
  project: TimelineProject,
): AudioClipSnapshot[] {
  // Compute the time window covered by the requested scenes
  const windowBounds = boundaries.filter(b => windowSceneIds.has(b.sceneId));
  if (windowBounds.length === 0) return [];
  const windowStart = Math.min(...windowBounds.map(b => b.startTime));
  const windowEnd = Math.max(...windowBounds.map(b => b.endTime));

  return clips
    .filter(clip => {
      const absStart = getClipAbsoluteStart(clip, project);
      const clipEnd = absStart + clip.duration;
      // Include if clip's playback range overlaps the window
      return absStart < windowEnd && clipEnd > windowStart;
    })
    .map(clip => {
      const absStart = getClipAbsoluteStart(clip, project);
      return {
        id: clip.id,
        startTime: absStart,
        duration: clip.duration,
        volume: clip.volume,
        bufferOffset: clip.bufferOffset ?? 0,
        lane: clip.lane,
        isMuted: clip.isMuted,
        fadeInTime: clip.fadeInTime,
        fadeOutTime: clip.fadeOutTime,
        sceneId: getSceneIdAtTime(boundaries, absStart),
      };
    });
}

/**
 * Get the scene boundaries relevant for the playTimeline message.
 * In scene view: just the active scene.
 * In All Shots mode: include ALL scenes. The clips array is metadata-only (tiny),
 * and Unity needs the full set to handle scene boundary advancement
 * during playback without additional bridge messages.
 */
export function getPlaybackSceneBoundaries(
  boundaries: SceneBoundary[],
  currentSceneId: number,
  isAllShotsMode: boolean,
): { windowSceneIds: Set<number>; sceneBoundaries: SceneBoundary[] } {
  if (!isAllShotsMode) {
    // Scene view: just the one scene
    const b = boundaries.find(b => b.sceneId === currentSceneId);
    return {
      windowSceneIds: new Set(b ? [b.sceneId] : []),
      sceneBoundaries: b ? [b] : [],
    };
  }

  // All Shots: include ALL scenes. The clips array is metadata-only (tiny),
  // and Unity needs the full set to handle scene boundary advancement
  // during playback without additional bridge messages.
  return {
    windowSceneIds: new Set(boundaries.map(b => b.sceneId)),
    sceneBoundaries: boundaries,
  };
}
